import { useTheme } from "@/contexts/ThemeContext";
import React from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SiteTranslator } from "@/components/LanguageSwitcher";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { TuningLoader } from "@/components/TuningLoader";
import ToolostCallbackHandler from "@/components/ToolostCallbackHandler";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/Auth";
// Route-level code splitting: each page loads only when visited.
// After a redeploy, old chunk names 404 — reload once to fetch the new build.
const lazyPage = (loader, name = "default") => React.lazy(() =>
  loader().then((m) => { sessionStorage.removeItem("dm_chunk_reload"); return { default: m[name] }; }).catch((err) => {
    if (!sessionStorage.getItem("dm_chunk_reload")) {
      sessionStorage.setItem("dm_chunk_reload", "1");
      window.location.reload();
    }
    throw err;
  })
);
const AppLayout = lazyPage(() => import("@/components/AppLayout"));
const AuthCallback = lazyPage(() => import("@/pages/AuthCallback"));
const ForgotPassword = lazyPage(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyPage(() => import("@/pages/ResetPassword"));
const Dashboard = lazyPage(() => import("@/pages/Dashboard"));
const Analytics = lazyPage(() => import("@/pages/Analytics"));
const ReleasesList = lazyPage(() => import("@/pages/ReleaseManager"), "ReleasesList");
const ReleaseForm = lazyPage(() => import("@/pages/ReleaseManager"), "ReleaseForm");
const AdminOverview = lazyPage(() => import("@/pages/AdminPanel"), "AdminOverview");
const AdminQueue = lazyPage(() => import("@/pages/AdminPanel"), "AdminQueue");
const AdminFinance = lazyPage(() => import("@/pages/AdminPanel"), "AdminFinance");
const AdminUsersPlus = lazyPage(() => import("@/pages/AdminUsersPlus"));
const AdminAnalytics = lazyPage(() => import("@/pages/AdminAnalytics"));
const AdminIntegrationsHub = lazyPage(() => import("@/pages/AdminIntegrationsHub"));
const AdminStorage = lazyPage(() => import("@/pages/AdminStorage"));
const AdminNotifications = lazyPage(() => import("@/pages/AdminNotifications"));
const AdminTickets = lazyPage(() => import("@/pages/AdminTickets"));
const AdminBlogs = lazyPage(() => import("@/pages/AdminContent"), "AdminBlogs");
const AdminDirectory = lazyPage(() => import("@/pages/AdminDirectory"));
const AdminSiteSettings = lazyPage(() => import("@/pages/AdminContent"), "AdminSiteSettings");
const AdminSEO = lazyPage(() => import("@/pages/AdminExtras"), "AdminSEO");
const AdminEmailBroadcast = lazyPage(() => import("@/pages/AdminExtras"), "AdminEmailBroadcast");
const BlogIndex = lazyPage(() => import("@/pages/Blog"), "BlogIndex");
const BlogPost = lazyPage(() => import("@/pages/Blog"), "BlogPost");
const Settings = lazyPage(() => import("@/pages/Settings"));
const Support = lazyPage(() => import("@/pages/Support"));
const Payments = lazyPage(() => import("@/pages/Payments"));
const AIStudio = lazyPage(() => import("@/pages/AIStudio"));
const Contact = lazyPage(() => import("@/pages/Contact"));
const SmartLinkPage = lazyPage(() => import("@/pages/SmartLinkPage"));
const ShortLinkRedirect = lazyPage(() => import("@/pages/SmartLinkPage"), "ShortLinkRedirect");
const SeoLanding = lazyPage(() => import("@/pages/SeoLanding"));
const Pricing = lazyPage(() => import("@/pages/Pricing"));
const ApiProduct = lazyPage(() => import("@/pages/ApiProduct"));
const ApiDocs = lazyPage(() => import("@/pages/ApiDocs"));
const WhiteLabel = lazyPage(() => import("@/pages/WhiteLabel"));
const Transparency = lazyPage(() => import("@/pages/Transparency"));
const About = lazyPage(() => import("@/pages/About"));
const AiDirectory = lazyPage(() => import("@/pages/AiDirectory"));
const AiSongPage = lazyPage(() => import("@/pages/AiSongPage"));
const Privacy = lazyPage(() => import("@/pages/Privacy"));
const Terms = lazyPage(() => import("@/pages/Terms"));
const CopyrightDispute = lazyPage(() => import("@/pages/CopyrightDispute"));
const Convert = lazyPage(() => import("@/pages/Convert"));
const Notifications = lazyPage(() => import("@/pages/Notifications"));
const LocalizedLanding = lazyPage(() => import("@/pages/LocalizedLanding"));
const CompareAiDistributors = lazyPage(() => import("@/pages/CompareAiDistributors"));

const PageFallback = () => <TuningLoader full />;
// WhatsApp support surface has been retired sitewide (Feb 2026). Distro AI
// is now the only support surface and lives inside the authenticated
// dashboard. The import is kept commented-out so future engineers know
// why the FAB isn't rendered anymore.
// import WhatsAppFAB from "@/components/WhatsAppFAB";

function Router() {
  const location = useLocation();
  // Every route change → scroll to the top. Prevents the "opens at the bottom" issue
  // when jumping from a long footer to /privacy or /terms.
  React.useEffect(() => {
    if (location.hash) return; // let in-page anchors work
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);
  // Social/search preview: brand banner on homepage only, logo everywhere else.
  React.useEffect(() => {
    const img = location.pathname === "/" ? "https://distributemusic.ai/og-banner-v2.jpg" : "https://distributemusic.ai/logo512.png";
    const big = location.pathname === "/";
    [['meta[property="og:image"]', img], ['meta[property="og:image:secure_url"]', img], ['meta[name="twitter:image"]', img],
     ['meta[property="og:image:width"]', big ? "1200" : "512"], ['meta[property="og:image:height"]', big ? "630" : "512"],
     ['meta[property="og:image:type"]', big ? "image/jpeg" : "image/png"]]
      .forEach(([sel, v]) => document.querySelector(sel)?.setAttribute("content", v));
  }, [location.pathname]);

  if (location.hash?.includes("session_id=")) return <React.Suspense fallback={<PageFallback />}><AuthCallback /></React.Suspense>;
  return (
    <React.Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/s/:id" element={<SmartLinkPage />} />
      <Route path="/l/:code" element={<ShortLinkRedirect />} />
      <Route path="/:code" element={<ShortLinkRedirect />} />
      <Route path="/distribute-suno-music" element={<SeoLanding pageKey="distribute-suno-music" />} />
      <Route path="/suno-to-spotify" element={<SeoLanding pageKey="suno-to-spotify" />} />
      <Route path="/distribute-udio-music" element={<SeoLanding pageKey="distribute-udio-music" />} />
      <Route path="/ai-music-distribution" element={<SeoLanding pageKey="ai-music-distribution" />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/music-distribution-api" element={<ApiProduct />} />
      <Route path="/docs/api" element={<ApiDocs />} />
      <Route path="/white-label-music-distribution" element={<WhiteLabel />} />
      <Route path="/transparency" element={<Transparency />} />
      <Route path="/about" element={<About />} />
      <Route path="/ai-music-directory" element={<AiDirectory />} />
      <Route path="/ai-song/:id" element={<AiSongPage />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/legal/copyright" element={<CopyrightDispute />} />
      <Route path="/dmca" element={<Navigate to="/legal/copyright#dmca-policy" replace />} />
      <Route path="/report-abuse" element={<Navigate to="/legal/copyright#report-abuse" replace />} />
      <Route path="/convert" element={<Convert />} />
      <Route path="/vs/distrokid-tunecore-cdbaby" element={<CompareAiDistributors />} />
      <Route path="/compare" element={<CompareAiDistributors />} />
      <Route path="/es" element={<LocalizedLanding />} />
      <Route path="/pt" element={<LocalizedLanding />} />
      <Route path="/hi" element={<LocalizedLanding />} />
      <Route path="/bn" element={<LocalizedLanding />} />
      <Route path="/id" element={<LocalizedLanding />} />
      <Route path="/tr" element={<LocalizedLanding />} />
      <Route path="/fr" element={<LocalizedLanding />} />
      <Route path="/de" element={<LocalizedLanding />} />
      <Route path="/ar" element={<LocalizedLanding />} />
      <Route path="/ja" element={<LocalizedLanding />} />
      <Route path="/ko" element={<LocalizedLanding />} />
      <Route path="/zh" element={<LocalizedLanding />} />
      <Route path="/ru" element={<LocalizedLanding />} />
      <Route path="/it" element={<LocalizedLanding />} />
      <Route path="/nl" element={<LocalizedLanding />} />
      <Route path="/pl" element={<LocalizedLanding />} />
      <Route path="/vi" element={<LocalizedLanding />} />
      <Route path="/th" element={<LocalizedLanding />} />
      <Route path="/fil" element={<LocalizedLanding />} />
      <Route path="/ms" element={<LocalizedLanding />} />
      <Route path="/uk" element={<LocalizedLanding />} />
      <Route path="/fa" element={<LocalizedLanding />} />
      <Route path="/ur" element={<LocalizedLanding />} />
      <Route path="/ta" element={<LocalizedLanding />} />
      <Route path="/te" element={<LocalizedLanding />} />
      <Route path="/mr" element={<LocalizedLanding />} />
      <Route path="/sw" element={<LocalizedLanding />} />
      <Route path="/ro" element={<LocalizedLanding />} />
      <Route path="/el" element={<LocalizedLanding />} />
      <Route path="/he" element={<LocalizedLanding />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/ai-studio" element={<AIStudio />} />
        <Route path="/releases" element={<ReleasesList />} />
        <Route path="/releases/new" element={<ReleaseForm />} />
        <Route path="/releases/:releaseId/edit" element={<ReleaseForm editing />} />
        <Route path="/releases/:releaseId" element={<ReleaseForm editing />} />
        <Route path="/analytics" element={<Analytics />} />
        {/* /wallet has been consolidated into /analytics — redirect any legacy links / bookmarks. */}
        <Route path="/wallet" element={<Navigate to="/analytics" replace />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/support" element={<Support />} />
        <Route path="/support/copyright" element={<CopyrightDispute embedded />} />
        <Route path="/settings" element={<Settings />} />
      </Route>

      <Route element={<ProtectedRoute adminOnly><AppLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<AdminOverview />} />
        <Route path="/admin/users" element={<AdminUsersPlus />} />
        <Route path="/admin/queue" element={<AdminQueue />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/insights" element={<Navigate to="/admin/analytics" replace />} />
        <Route path="/admin/integrations" element={<AdminIntegrationsHub />} />
        <Route path="/admin/storage" element={<AdminStorage />} />
        <Route path="/admin/catalog" element={<Navigate to="/admin/integrations?tab=catalog" replace />} />
        <Route path="/admin/notifications" element={<AdminNotifications />} />
        <Route path="/admin/tickets" element={<AdminTickets />} />
        <Route path="/admin/blog" element={<AdminBlogs />} />
        <Route path="/admin/directory" element={<AdminDirectory />} />
        <Route path="/admin/site" element={<AdminSiteSettings />} />
        <Route path="/admin/seo" element={<AdminSEO />} />
        <Route path="/admin/acrcloud" element={<Navigate to="/admin/integrations?tab=acrcloud" replace />} />
        <Route path="/admin/email" element={<AdminEmailBroadcast />} />
        <Route path="/admin/finance" element={<AdminFinance />} />
        <Route path="/admin/toolost" element={<Navigate to="/admin/integrations?tab=toolost" replace />} />
      </Route>
    </Routes>
    </React.Suspense>
  );
}

// Toasts follow the app's light / dark theme.
function ThemedToaster() {
  const { theme } = useTheme();
  const light = theme === "light";
  return <Toaster theme={light ? "light" : "dark"} position="top-right" toastOptions={{ style: light
    ? { background: "#FFFFFF", border: "1px solid #E4E4E7", color: "#18181B" }
    : { background: "#0F0F11", border: "1px solid #27272A", color: "#F4F4F5" } }} />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <ToolostCallbackHandler />
            <ImpersonationBanner />
            <Router />
            <SiteTranslator />
            <ThemedToaster />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
